import {
  GridApi,
  GridColumnGroupHeaderParams,
  GridColumnGroupingModel,
  GridValidRowModel,
} from '@mui/x-data-grid-pro';
import { Assessment, AssessmentWithEntries } from '@schooly/api';
import React from 'react';

import { OnColumnMouseOverProps } from '../../../../context/table/dataGridHover/WithDataGridHover';
import {
  createMarkbookForGroupFieldKey,
  createMarkbookForGroupsAverageFieldKey,
  createMarkbookForGroupsMethodFieldKey,
} from './assessmentFieldKeys';
import {
  ASSESSMENT_CSS_CLASSES,
  BASELINE_COL_FIELD_KEY,
  getAssessmentColumnWidth,
  STUDENT_COL_FIELD_KEY,
} from './assessmentStyling';
import { FixedHorizontalHeaderCell, MarkbookForGroupsAverageHeaderCell } from './MarkbookCell';
import {
  MarkbookForGroupsAssessmentHeaderCell,
  MarkbookForGroupsStudentsHeaderCell,
  MarkbookForGroupsStudentsHeaderCellProps,
  MarkbookForGroupsSubjectHeaderCell,
} from './MarkbookCell';
import { MarkbookBaseline } from './types';
import { getMarkbookSubjects } from './utils';
interface CreateMarkbookForGroupColumnGroupingParams {
  assessments: AssessmentWithEntries[];
  groupId?: string;
}

export const createMarkbookForGroupColumnGrouping = ({
  assessments,
  groupId = STUDENT_COL_FIELD_KEY,
}: CreateMarkbookForGroupColumnGroupingParams): GridColumnGroupingModel => {
  const studentColumnGroup = {
    groupId,
    children: [{ field: groupId }],
    editable: false,
    headerClassName: `${ASSESSMENT_CSS_CLASSES.studentsHeaderCell} ${ASSESSMENT_CSS_CLASSES.withBottomBorder}`,
  };

  const assessmentColumnGroups = assessments.flatMap((assessment, assessmentIndex) => ({
    groupId: assessment.id,
    headerClassName: `${ASSESSMENT_CSS_CLASSES.assessmentInfoHeaderCell} ${ASSESSMENT_CSS_CLASSES.withBottomBorder}`,
    children: assessment.methods.map((method, methodIndex) => ({
      field: createMarkbookForGroupFieldKey({
        assessmentId: assessment.id,
        assessmentIndex,
        methodIndex,
        methodType: method.method_type,
      }),
    })),
  }));

  return [studentColumnGroup, ...assessmentColumnGroups];
};

interface CreateMarkbookForGroupsColumnGroupingParams {
  assessments: Assessment[];
  baseline?: MarkbookBaseline;
  onColumnMouseOver: (props: OnColumnMouseOverProps) => void;
  onMouseLeave: () => void;
  apiRef: React.MutableRefObject<GridApi>;
  rows: GridValidRowModel[];
  groupId?: string;
  studentsHeaderCellProps: MarkbookForGroupsStudentsHeaderCellProps;
  showStudentsAverage?: boolean;
}

const createStudentColumnGroup = ({
  groupId,
  studentsHeaderCellProps,
  headerClassName = ASSESSMENT_CSS_CLASSES.studentsHeaderCell,
}: {
  groupId: string;
  studentsHeaderCellProps: MarkbookForGroupsStudentsHeaderCellProps;
  headerClassName?: string;
}) => {
  return {
    groupId,
    children: [{ field: groupId }],
    headerClassName,
    renderHeaderGroup: () => <MarkbookForGroupsStudentsHeaderCell {...studentsHeaderCellProps} />,
  };
};

const createMarkbookForGroupsBaselineColumnGroup = ({
  groupId,
  headerClassName = ASSESSMENT_CSS_CLASSES.baselineHeaderCell,
  ...baseline
}: MarkbookBaseline & {
  headerClassName?: string;
  groupId: string;
}) => {
  return {
    groupId: `${groupId}-container`,
    headerClassName,
    renderHeaderGroup: () => <MarkbookForGroupsSubjectHeaderCell subject={baseline.subject} />,
    children: [
      {
        groupId,
        editable: false,
        headerClassName: `${ASSESSMENT_CSS_CLASSES.assessmentInfoHeaderCell} ${ASSESSMENT_CSS_CLASSES.withTopBorder}`,
        renderHeaderGroup: (props: GridColumnGroupHeaderParams) => {
          switch (baseline.kind) {
            case 'assessment':
              return (
                <MarkbookForGroupsAssessmentHeaderCell
                  assessmentDateVisible={false}
                  assessment={baseline.assessment}
                />
              );
            case 'average':
              return <MarkbookForGroupsAverageHeaderCell />;
          }
        },
        children: [{ field: groupId }],
      },
    ],
  };
};

export const createMarkbookArrangeByAssessmentForGroupsColumnGrouping = ({
  assessments,
  onColumnMouseOver,
  onMouseLeave,
  apiRef,
  rows,
  groupId = STUDENT_COL_FIELD_KEY,
  studentsHeaderCellProps,
}: CreateMarkbookForGroupsColumnGroupingParams): GridColumnGroupingModel => {
  const assessmentColumnGroups = assessments.flatMap(
    (assessment, assessmentIndex, assessmentArray) => {
      const isLastAssessment = assessmentIndex === assessmentArray.length - 1;
      const subjects = getMarkbookSubjects(assessment.groups);
      return {
        groupId: assessment.id,
        editable: false,
        headerClassName: ASSESSMENT_CSS_CLASSES.assessmentInfoHeaderCell,
        renderHeaderGroup: (props: GridColumnGroupHeaderParams) => (
          <MarkbookForGroupsAssessmentHeaderCell
            onMouseOver={(event: React.MouseEvent<HTMLElement, MouseEvent>) =>
              onColumnMouseOver({
                getCellElement: apiRef.current.getCellElement,
                colFields: props.fields,
                cellSelector: `.${ASSESSMENT_CSS_CLASSES.assessmentInfoHeaderCell}`,
                event,
                rows,
              })
            }
            onMouseLeave={onMouseLeave}
            assessment={assessment}
          />
        ),
        children: subjects.map((subject, subjectIndex, subjectArray) => {
          const isLastSubject = subjectIndex === subjectArray.length - 1;
          return {
            groupId: `${assessment.id}-${subject.id}`,
            editable: false,
            headerClassName:
              !isLastAssessment && isLastSubject
                ? [
                    ASSESSMENT_CSS_CLASSES.subjectHeaderCell,
                    ASSESSMENT_CSS_CLASSES.withRightBorder,
                  ].join(' ')
                : ASSESSMENT_CSS_CLASSES.subjectHeaderCell,
            renderHeaderGroup: () => <MarkbookForGroupsSubjectHeaderCell subject={subject} />,

            children: assessment.methods.map((method) => ({
              field: createMarkbookForGroupsMethodFieldKey({
                assessmentId: assessment.id,
                subjectId: subject.id,
                methodType: method.method_type,
              }),
            })),
          };
        }),
      };
    },
  );

  return [
    createStudentColumnGroup({ groupId, studentsHeaderCellProps }),
    ...assessmentColumnGroups,
  ];
};

export const createMarkbookArrangeBySubjectForGroupsColumnGrouping = ({
  assessments,
  onColumnMouseOver,
  onMouseLeave,
  apiRef,
  rows,
  groupId = STUDENT_COL_FIELD_KEY,
  studentsHeaderCellProps,
  baseline,
  showStudentsAverage,
}: CreateMarkbookForGroupsColumnGroupingParams): GridColumnGroupingModel => {
  const subjects = getMarkbookSubjects(assessments.flatMap((assessment) => assessment.groups));

  const assessmentColumnGroups = subjects.flatMap((subject, subjectIndex, subjectsArray) => {
    const subjectGroupsIds = subject.groups.map(({ id }) => id);
    const subjectAssessments = assessments.filter((assessment) =>
      assessment.groups.some(({ id }) => subjectGroupsIds.includes(id)),
    );

    const prevSubjectsWidth = subjectsArray.slice(0, subjectIndex).reduce((acc, subject) => {
      const subjectGroupsIds = subject.groups.map(({ id }) => id);
      const subjectAssessmentsColumnWidth = assessments
        .filter((assessment) => assessment.groups.some(({ id }) => subjectGroupsIds.includes(id)))
        .reduce((acc, assessment) => acc + getAssessmentColumnWidth(assessment.methods.length), 0);
      return acc + subjectAssessmentsColumnWidth;
    }, 0);

    const children = subjectAssessments.map((assessment) => {
      return {
        groupId: `${assessment.id}-${subject.id}`,
        headerClassName: `${ASSESSMENT_CSS_CLASSES.assessmentInfoHeaderCell} ${ASSESSMENT_CSS_CLASSES.withTopBorder}`,
        renderHeaderGroup: (props: GridColumnGroupHeaderParams) => (
          <MarkbookForGroupsAssessmentHeaderCell
            onMouseOver={(event: React.MouseEvent<HTMLElement, MouseEvent>) =>
              onColumnMouseOver({
                getCellElement: apiRef.current.getCellElement,
                colFields: props.fields,
                cellSelector: `.${ASSESSMENT_CSS_CLASSES.assessmentInfoHeaderCell}`,
                event,
                rows,
              })
            }
            onMouseLeave={onMouseLeave}
            assessment={assessment}
          />
        ),
        children: assessment.methods.map((method) => ({
          field: createMarkbookForGroupsMethodFieldKey({
            assessmentId: assessment.id,
            subjectId: subject.id,
            methodType: method.method_type,
          }),
        })),
      };
    });

    const subjectColumnWidth = subjectAssessments.reduce(
      (acc, assessment) => acc + getAssessmentColumnWidth(assessment.methods.length),
      0,
    );

    return {
      groupId: subject.id,
      headerClassName: [
        ASSESSMENT_CSS_CLASSES.subjectHeaderCell,
        ASSESSMENT_CSS_CLASSES.overflowVisible,
        ASSESSMENT_CSS_CLASSES.withRightBorder,
      ].join(' '),
      renderHeaderGroup: () => (
        <FixedHorizontalHeaderCell width={subjectColumnWidth} offsetLeft={prevSubjectsWidth}>
          <MarkbookForGroupsSubjectHeaderCell subject={subject} />
        </FixedHorizontalHeaderCell>
      ),
      children: [
        ...children,
        ...(showStudentsAverage ? [createAverageColumnGroup({ subjectId: subject.id })] : []),
      ],
    };
  });

  return [
    createStudentColumnGroup({ groupId, studentsHeaderCellProps }),
    ...(baseline
      ? [
          createMarkbookForGroupsBaselineColumnGroup({
            ...baseline,
            groupId: BASELINE_COL_FIELD_KEY,
          }),
        ]
      : []),
    ...assessmentColumnGroups,
  ];
};

const createAverageColumnGroup = ({
  subjectId,
  headerClassName = ASSESSMENT_CSS_CLASSES.averageHeaderCell,
}: {
  subjectId: string;
  headerClassName?: string;
}) => {
  return {
    groupId: createMarkbookForGroupsAverageFieldKey({ subjectId }),
    children: [{ field: createMarkbookForGroupsAverageFieldKey({ subjectId }) }],
    headerClassName,
    renderHeaderGroup: () => <MarkbookForGroupsAverageHeaderCell />,
  };
};
